import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixReceiptsPresetsV1ReceiptPresetsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/receipt-presets',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/receipts/v1/receipt-presets',
        destPath: '/v1/receipt-presets',
      },
    ],
    _: [
      {
        srcPath: '/receipt-presets/v1/receipt-presets',
        destPath: '/v1/receipt-presets',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/receipt-presets/v1/receipt-presets',
        destPath: '/v1/receipt-presets',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_get-paid_receipt-presets';

/** Creates a receipt preset. */
export function createReceiptPreset(
  payload: object
): RequestOptionsFactory<any> {
  function __createReceiptPreset({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'receiptPreset.createdDate' },
          { path: 'receiptPreset.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'POST' as any,
      methodFqn: 'wix.receipts.presets.v1.ReceiptPresets.CreateReceiptPreset',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPreset.createdDate' },
              { path: 'receiptPreset.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createReceiptPreset;
}

/** Retrieves a receipt preset. */
export function getReceiptPreset(payload: object): RequestOptionsFactory<any> {
  function __getReceiptPreset({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'GET' as any,
      methodFqn: 'wix.receipts.presets.v1.ReceiptPresets.GetReceiptPreset',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets/{receiptPresetId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPreset.createdDate' },
              { path: 'receiptPreset.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getReceiptPreset;
}

/**
 * Retrieves the default receipt preset.
 *
 * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
 */
export function getDefaultReceiptPreset(
  payload: object
): RequestOptionsFactory<any> {
  function __getDefaultReceiptPreset({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'GET' as any,
      methodFqn:
        'wix.receipts.presets.v1.ReceiptPresets.GetDefaultReceiptPreset',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets/default',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPreset.createdDate' },
              { path: 'receiptPreset.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDefaultReceiptPreset;
}

/**
 * Updates a receipt preset.
 *
 *
 * Each time the receipt preset is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the receipt preset.
 * This ensures you're working with the latest receipt preset
 * and prevents unintended overwrites.
 */
export function updateReceiptPreset(
  payload: object
): RequestOptionsFactory<any> {
  function __updateReceiptPreset({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'receiptPreset.createdDate' },
          { path: 'receiptPreset.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'PATCH' as any,
      methodFqn: 'wix.receipts.presets.v1.ReceiptPresets.UpdateReceiptPreset',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets/{receiptPreset.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPreset.createdDate' },
              { path: 'receiptPreset.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateReceiptPreset;
}

/**
 * Sets a receipt preset as default.
 *
 * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
 */
export function setDefaultReceiptPreset(
  payload: object
): RequestOptionsFactory<any> {
  function __setDefaultReceiptPreset({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'POST' as any,
      methodFqn:
        'wix.receipts.presets.v1.ReceiptPresets.SetDefaultReceiptPreset',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets/default/{receiptPresetId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPreset.createdDate' },
              { path: 'receiptPreset.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setDefaultReceiptPreset;
}

/** Permanently deletes a receipt preset. */
export function deleteReceiptPreset(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteReceiptPreset({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'DELETE' as any,
      methodFqn: 'wix.receipts.presets.v1.ReceiptPresets.DeleteReceiptPreset',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets/{receiptPresetId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteReceiptPreset;
}

/**
 * Lists all available receipt presets.
 *
 * By default the returned receipt presets are sorted by `default` and `updatedDate` in descending order.
 */
export function listReceiptPresets(
  payload: object
): RequestOptionsFactory<any> {
  function __listReceiptPresets({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'GET' as any,
      methodFqn: 'wix.receipts.presets.v1.ReceiptPresets.ListReceiptPresets',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPresets.createdDate' },
              { path: 'receiptPresets.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listReceiptPresets;
}

/** Updates the extended fields of a receipt preset, without incrementing revision. */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.receipts.presets.v1.receipt_preset',
      method: 'POST' as any,
      methodFqn: 'wix.receipts.presets.v1.ReceiptPresets.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixReceiptsPresetsV1ReceiptPresetsUrl({
        protoPath: '/v1/receipt-presets/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'receiptPreset.createdDate' },
              { path: 'receiptPreset.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}
